import React, { useState } from 'react';
import {
	Box,
	VStack,
	Flex,
	HStack,
	Text,
	Center,
	Button,
	SimpleGrid,
	Badge,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Avatar,
	Spinner,
} from '@chakra-ui/react';
import {
	FaUpload,
	FaDownload,
	FaUser,
	FaAddressBook,
	FaList,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as contactApi from '../../../Api/contactApi';
import ContactSourceBadge from '../../Contacts/ContactSourceBadge';
import { SectionCard, StatTile, EmptyPanel } from './dashboardShared';

const ContactsTab = ({ lists = [], companyId }) => {
	const navigate = useNavigate();
	const [selectedList, setSelectedList] = useState(null);

	const { data: contactsData, isLoading: contactsLoading } = useQuery({
		queryKey: ['contacts', selectedList?._id, companyId],
		queryFn: () => {
			if (!selectedList?._id) return null;
			return contactApi.getContactsByListId(selectedList._id, companyId, {
				limit: 10,
			});
		},
		enabled: !!selectedList?._id,
	});

	const contacts = contactsData?.data?.contacts || [];
	const totalContacts =
		lists?.reduce((sum, list) => sum + (list.totalContacts || 0), 0) || 0;
	const activeContacts =
		lists?.reduce((sum, list) => sum + (list.activeContacts || 0), 0) || 0;

	return (
		<VStack spacing={6} align='stretch'>
			<SectionCard
				title='Contact management'
				description='Lists, imports, and recent contacts from your audience.'
				action={
					<HStack>
						<Button
							leftIcon={<FaUpload />}
							variant='outline'
							size='sm'
							onClick={() => navigate('/contacts/import/upload')}>
							Import
						</Button>
						<Button
							leftIcon={<FaDownload />}
							variant='outline'
							size='sm'
							onClick={() => navigate('/contacts')}>
							Open contacts
						</Button>
						<Button
							leftIcon={<FaUser />}
							colorScheme='teal'
							size='sm'
							onClick={() => navigate('/contacts')}>
							Manage contacts
						</Button>
					</HStack>
				}>
				<SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
					<StatTile
						title='Total contacts'
						value={totalContacts.toLocaleString()}
						icon={FaAddressBook}
						gradient='linear(135deg, blue.400, blue.600)'
						bgGradient='linear(blue.400, blue.600)'
					/>
					<StatTile
						title='Contact lists'
						value={lists?.length || 0}
						icon={FaList}
						gradient='linear(135deg, green.400, green.600)'
						bgGradient='linear(green.400, green.600)'
					/>
					<StatTile
						title='Active contacts'
						value={activeContacts.toLocaleString()}
						icon={FaUser}
						gradient='linear(135deg, purple.400, purple.600)'
						bgGradient='linear(purple.400, purple.600)'
					/>
				</SimpleGrid>
			</SectionCard>

			<SectionCard title='Contact lists' description='Select a list to preview contacts.'>
				{!lists || lists.length === 0 ? (
					<EmptyPanel
						message='No contact lists found.'
						actionLabel='Open contacts'
						onAction={() => navigate('/contacts')}
					/>
				) : (
					<SimpleGrid columns={{ base: 1, md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} spacing={4}>
						{lists.map((list) => (
							<Box
								key={list._id}
								as='button'
								type='button'
								textAlign='left'
								w='full'
								cursor='pointer'
								onClick={() => setSelectedList(list)}
								border='1px solid'
								borderColor={
									selectedList?._id === list._id ? 'teal.400' : 'gray.200'
								}
								borderRadius='xl'
								p={4}
								bg={selectedList?._id === list._id ? 'teal.50' : 'white'}
								_hover={{ borderColor: 'teal.300', bg: 'teal.50' }}>
								<Flex justify='space-between' align='start' gap={3}>
									<Box>
										<Text fontWeight='semibold'>{list.listName}</Text>
										<Text fontSize='xs' color='gray.500' mt={1}>
											Created {new Date(list.createdAt).toLocaleDateString()}
										</Text>
										<Text fontSize='xs' color='gray.500'>
											Active: {list.activeContacts || 0}
										</Text>
									</Box>
									<Badge colorScheme='teal' borderRadius='full'>
										{list.totalContacts || 0}
									</Badge>
								</Flex>
							</Box>
						))}
					</SimpleGrid>
				)}
			</SectionCard>

			{selectedList && (
				<SectionCard
					title={`Contacts in "${selectedList.listName}"`}
					description='Showing up to 10 contacts from the selected list.'>
					{contactsLoading ? (
						<Center py={8}>
							<Spinner color='teal.500' />
						</Center>
					) : contacts.length === 0 ? (
						<EmptyPanel message='No contacts in this list yet.' />
					) : (
						<Table variant='simple' size='sm'>
							<Thead>
								<Tr>
									<Th>Contact</Th>
									<Th>Email</Th>
									<Th>Status</Th>
									<Th>Source</Th>
									<Th>Added</Th>
								</Tr>
							</Thead>
							<Tbody>
								{contacts.map((contact) => (
									<Tr key={contact._id}>
										<Td>
											<HStack>
												<Avatar
													size='sm'
													name={`${contact.firstName} ${contact.lastName}`}
												/>
												<VStack align='start' spacing={0}>
													<Text fontSize='sm' fontWeight='semibold'>
														{contact.firstName} {contact.lastName}
													</Text>
													{contact.companyName && (
														<Text fontSize='xs' color='gray.500'>
															{contact.companyName}
														</Text>
													)}
												</VStack>
											</HStack>
										</Td>
										<Td>{contact.email}</Td>
										<Td>
											<Badge
												colorScheme={
													contact.status === 'active' ? 'green' : 'yellow'
												}
												borderRadius='full'>
												{contact.status || 'unknown'}
											</Badge>
										</Td>
										<Td>
											<ContactSourceBadge source={contact.createdBySource} />
										</Td>
										<Td>
											{contact.createdAt
												? new Date(contact.createdAt).toLocaleDateString()
												: '—'}
										</Td>
									</Tr>
								))}
							</Tbody>
						</Table>
					)}
				</SectionCard>
			)}
		</VStack>
	);
};

export default ContactsTab;
